import { createRouter, createWebHistory, RouteLocationNormalized } from 'vue-router';
import { basicRoutes, permissionRoutes } from './routes';
import { usePermissionStore } from '@/stores/Permission';
import { PermissionType, getAllPermissionTypes } from './types';
import MainLayout from '@/Layout/MainLayout.vue';

// 处理路由组件，避免响应式问题
const RawMainLayout = markRaw(MainLayout);

// 处理基础路由组件
const processedBasicRoutes = basicRoutes.map(route => {
  if (route.component === MainLayout) {
    return { ...route, component: RawMainLayout };
  }
  if (route.children) {
    return {
      ...route,
      children: route.children.map(child => 
        child.component === MainLayout 
          ? { ...child, component: RawMainLayout } 
          : child
      )
    };
  }
  return route;
});

// 创建路由实例
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: processedBasicRoutes
});

// 路由守卫 - 权限控制
router.beforeEach(async (to, from, next) => {
  // 1. 登录页直接放行
  if (to.path === '/login') {
    next();
    return;
  }
  
  // 2. 检查登录状态
  const userInfoStr = sessionStorage.getItem('userInfo');
  if (!userInfoStr) {
    next('/login');
    return;
  }

  // 3. 解析用户信息
  let userData;
  try {
    userData = JSON.parse(userInfoStr);
  } catch (error) {
    console.error('解析用户信息失败:', error);
    next('/login');
    return;
  }

  // 4. 权限处理
  const permissionStore = usePermissionStore();
  
  // 5. 首次加载权限路由
  if (permissionStore.accessRoutes.length === 0) {
    let userPermissions: PermissionType[] = [];
    
    // 根据schoolId判断权限
    if (userData.schoolId === null) {
      userPermissions = getAllPermissionTypes();
    } else {
      userPermissions = [
        PermissionType.SchoolManage,
        PermissionType.UserEvaluation,
        PermissionType.StatisticHome
      ];
    }
    
    // 设置权限并生成路由
    permissionStore.setPermissions(userPermissions);
    
    // 动态添加路由
    permissionStore.accessRoutes.forEach(route => {
      const processedRoute = processRouteComponent(route);
      router.addRoute(processedRoute);
    });
    
    // 重新导航
    next({ path: to.path || '/', replace: true });
  }

  // 6. 检查权限访问（整合原第二个守卫的逻辑）
  const requiredPermission = to.meta.permission;
  if (requiredPermission) {
    const hasPermission = Array.isArray(requiredPermission)
      ? requiredPermission.some(perm => permissionStore.permissions.includes(perm))
      : permissionStore.permissions.includes(requiredPermission);
      
    if (!hasPermission) {
      next('/403');
      return;
    }
  }

  // 7. 所有检查通过
  next();
});

/**
 * 处理路由组件，避免响应式
 */
function processRouteComponent(route: any): any {
  if (route.component && !route.component.__v_skip) {
    route.component = markRaw(route.component);
  }
  
  if (route.children && route.children.length > 0) {
    route.children = route.children.map((child: any) => processRouteComponent(child));
  }
  
  return route;
}

/**
 * 检查路由访问权限
 */
function checkPermission(route: RouteLocationNormalized): boolean {
  // 基础路由不需要权限
  if (basicRoutes.some(r => r.path === route.path)) {
    return true;
  }

  // 无权限要求的路由默认允许访问
  const { meta } = route;
  if (!meta || !meta.permission) {
    return true;
  }

  // 检查权限
  const permissionStore = usePermissionStore();
  const userPermissions = permissionStore.permissions;

  if (Array.isArray(meta.permission)) {
    return meta.permission.some(permission => 
      userPermissions.includes(permission)
    );
  } else {
    return userPermissions.includes(meta.permission);
  }
}
export default router;